import { useEffect, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';

import { IrlEventTrackingEvent } from 'src/gql/graphql';
import useUser from 'hooks/use-user';
import { useAccountSignupAnalytics, useIsAccountSignupAnalyticsEnabled } from 'src/hooks/use-account-signup-analytics';
import { normalizeIsNewUser } from 'src/oauth/append-is-new-user';
import type { AccountSignupMethod } from 'src/utils/analytics/events';
import type { AuthOptions } from './use-irl-event-auth';

// Default fallback in minutes
const DEFAULT_ACCOUNT_AGE_THRESHOLD_MINUTES = 5;

type UseSocialAuthTrackingParams = {
  handleAuthSuccess: (options: AuthOptions) => Promise<void>;
};

/**
 * Determines if a user is newly created based on their createdAt timestamp.
 * Returns true if user was created within the specified time window (in minutes).
 */
export function checkNewUser(createdAt: string | null, windowMinutes: number): boolean {
  if (!createdAt) {
    return false;
  }
  const windowMs = windowMinutes * 60 * 1000;
  return Date.now() - new Date(createdAt).getTime() < windowMs;
}

const SOCIAL_SIGNUP_METHODS: AccountSignupMethod[] = ['google', 'apple'];

const parseSocialSignupMethod = (value: string[] | string | undefined): AccountSignupMethod | null => {
  const raw = Array.isArray(value) ? value[value.length - 1] : value;
  return SOCIAL_SIGNUP_METHODS.includes(raw as AccountSignupMethod) ? (raw as AccountSignupMethod) : null;
};

/**
 * Detects when a user returns from social auth redirect and handles post-auth flow.
 * Uses URL state (authSource=social) as the guard - removing it after handling prevents duplicates.
 * Determines if user is new (signup) or existing (login) based on account age.
 * Callback from useIrlEventFlow that creates DPOS customer and tracks auth event.
 */
export function useSocialAuthTracking({ handleAuthSuccess }: UseSocialAuthTrackingParams): void {
  const router = useRouter();
  const user = useUser();
  const flags = useFlags();

  const ecommUserId = useObserver(() => user.id) ?? '';
  const userCreatedAt = useObserver(() => user.createdAt);

  const thresholdMinutes =
    (flags['ecomm.irl-events.account-age-threshold-minutes.config'] as number | undefined) ??
    DEFAULT_ACCOUNT_AGE_THRESHOLD_MINUTES;

  // authMethod is set on the IRL social redirect URL per provider, so the backend-confirmed
  // isNewUser signal that returns with it can be attributed to a signup method.
  const authQuery = router?.query ?? {};
  const socialSignupMethod = parseSocialSignupMethod(authQuery.authMethod);
  const isBackendConfirmedNewUser = normalizeIsNewUser(authQuery.isNewUser) === 'true';
  const { trackSucceeded } = useAccountSignupAnalytics({ surface: 'irlEvent', method: socialSignupMethod });
  const isSignupAnalyticsEnabled = useIsAccountSignupAnalyticsEnabled();
  const hasTrackedSignupRef = useRef(false);

  useEffect(() => {
    if (!router?.isReady || !ecommUserId) {
      return;
    }

    let remainingQuery = router.query ?? {};

    // Existing IRL telemetry: gated on the pre-existing authSource marker, behavior unchanged.
    if (router.query?.authSource === 'social') {
      const isNewUser = checkNewUser(userCreatedAt, thresholdMinutes);
      const authEvent = isNewUser ? IrlEventTrackingEvent.signup : IrlEventTrackingEvent.login;

      void handleAuthSuccess({ authEvent });
      remainingQuery = _.omit(remainingQuery, 'authSource');
    }

    // Analytics succeeded: gated on the rollout flag but NOT on authSource, so a flag that streams
    // in after the authSource strip can still emit once. Only the backend-confirmed net-new case
    // counts; absent/false isNewUser means existing-account login or an unattributable return.
    if (
      !hasTrackedSignupRef.current &&
      isSignupAnalyticsEnabled &&
      isBackendConfirmedNewUser &&
      socialSignupMethod !== null
    ) {
      hasTrackedSignupRef.current = true;
      trackSucceeded();
      remainingQuery = _.omit(remainingQuery, ['authMethod', 'isNewUser']);
    }

    if (!_.isEqual(remainingQuery, router.query)) {
      void router.replace({ pathname: router.pathname, query: remainingQuery }, undefined, { shallow: true });
    }
  }, [
    router,
    handleAuthSuccess,
    ecommUserId,
    userCreatedAt,
    thresholdMinutes,
    isSignupAnalyticsEnabled,
    isBackendConfirmedNewUser,
    socialSignupMethod,
    trackSucceeded,
  ]);
}
